import {
  ARROW_LEFT,
  ARROW_RIGHT,
  HIDDEN_SELECTOR,
  deactivate,
  isActive,
  isCapturable,
  isCloseKey,
  isDownKey,
  isHorizontalArrowKey,
  isNavigable,
  isSelectionKey,
  isShiftableKey,
  isVerticalArrowKey,
  isVerticalMovementKey,
  updateElementFocus,
} from '../../../utils/keyboard-nav-support';
import { indexInRange } from '../../../utils/spds/array-handling';
import { isEmpty } from '@ember/utils';
import { modifier } from 'ember-modifier';
import { or, reads } from 'macro-decorators';
import { tracked } from '@glimmer/tracking';
import { waitForLater, waitForNext } from '../../../utils/waiters';
import Super from '../core/base.gjs';

const MENU_OPEN_DELAY = 100;

export default class SpdsInputKeyboardNavigable extends Super {
  @tracked _value;
  @tracked open = false;
  @tracked selected;
  @tracked triggerComponent;

  @or('isMultiselect', 'search') allowHoritzontalNavigation;

  @reads('triggerComponent.clearButton') clearButton;
  @reads('triggerComponent.clearFilterComponent.clearButton') clearFilter;
  @reads('args.multiselect') isMultiselect;
  @reads('triggerComponent.trigger') trigger;

  get clearFunctionIsActive() {
    return this.allowHoritzontalNavigation
      ? this.clearButtons?.some(button => isActive(button))
      : false;
  }

  get clearButtons() {
    let buttons = [];

    if (this.clearButton) buttons.push(this.clearButton);
    if (this.clearFilter) buttons.push(this.clearFilter);

    return buttons;
  }

  get navigableOptions() {
    return this.menuNavigableOptions.filter(el => {
      let element = this.isMultiselect ? el.parentElement : el;
      return !element.classList.contains(HIDDEN_SELECTOR);
    });
  }

  get value() {
    return (this._value ?? this.args.value) || '';
  }

  set value(newValue) {
    this.currentValue = newValue;
    this._value = newValue;
  }

  updateValueFromExternalSource = modifier(async () => {
    let { value } = this.args;

    if (!(this.isCurrentValue(value) && isEmpty(value))) {
      await this.updateValue(value);
    }
  });

  willDestroy() {
    super.willDestroy(...arguments);

    this.baseElement.removeEventListener('keydown', this.keyboardNav);
  }

  canTriggerKeyboardOpen(key) {
    return (isVerticalArrowKey(key) || isSelectionKey(key)) && (this.menuCanOpen ?? true);
  }

  deactivateFilterElements() {
    this.clearButtons.forEach(button => deactivate(button));
  }

  determineDirection({ key, shiftKey }) {
    return isDownKey(key) && !(shiftKey && isShiftableKey(key)) ? 1 : -1;
  }

  findActiveElementIndex(elements) {
    return elements.findIndex(isActive);
  }

  focusActiveOption() {
    this.deactivateFilterElements();
    updateElementFocus(this.activeOption);
  }

  focusClearButton() {
    deactivate(this.activeOption);
    updateElementFocus(this.clearButton);
  }

  focusClearFilter() {
    deactivate(this.activeOption);
    updateElementFocus(this.clearFilter);
  }

  focusTrigger() {
    this.trigger.focus();
  }

  getActiveOption() {
    if (isActive(this.clearFilter)) {
      return this.clearFilter;
    } else if (isActive(this.clearButton)) {
      return this.clearButton;
    } else {
      return this.navigableOptions?.find(isActive) || this.trigger;
    }
  }

  hasCustomAction(key) {
    if (isHorizontalArrowKey(key) && !this.allowHoritzontalNavigation) return false;
    if (isHorizontalArrowKey(key) && this.allowHoritzontalNavigation) {
      return (
        this.clearFunctionIsActive ||
        isNavigable(this.clearFilter?.parentElement) ||
        isNavigable(this.clearButton)
      );
    }

    return true;
  }

  handleHorizontalPress({ key }) {
    switch (key) {
      case ARROW_LEFT: {
        if (this.clearFunctionIsActive && this.activeOption) {
          this.focusActiveOption();
        } else if (this.clearFunctionIsActive && !this.activeOption) {
          this.deactivateFilterElements();
          this.focusTrigger();
        } else if (isNavigable(this.clearFilter?.parentElement)) {
          this.focusClearFilter();
        } else if (isNavigable(this.clearButton)) {
          this.focusClearButton();
        }
        break;
      }
      case ARROW_RIGHT: {
        if (this.clearFunctionIsActive && this.activeOption) {
          this.focusActiveOption();
        } else if (this.clearFunctionIsActive && !this.activeOption) {
          this.deactivateFilterElements();
          this.focusTrigger();
        } else if (isNavigable(this.clearButton)) {
          this.focusClearButton();
        } else if (isNavigable(this.clearFilter?.parentElement)) {
          this.focusClearFilter();
        }
        break;
      }
    }
  }

  handleVerticalPress(evt) {
    let activeElement = this.getActiveOption();
    let modifier = this.determineDirection(evt);
    let startingIndex = this.navigableOptions.findIndex(option => option === activeElement);

    if (indexInRange(this.navigableOptions.length, startingIndex)) {
      deactivate(this.navigableOptions[startingIndex]);
    }

    if (this.clearButtons.includes(activeElement)) {
      this.deactivateFilterElements();
      this.updateVerticalSelection(
        this.navigableOptions.findIndex(option => option === this.activeOption)
      );
    } else {
      this.updateVerticalSelection(startingIndex + modifier);
    }
  }

  isCurrentValue(value) {
    return value === this.currentValue;
  }

  keyboardNav = evt => {
    if (this.args.disabled) return;

    if (!this.open) {
      this.openWithKeyboard(evt);
    } else {
      this.parseKeyAction(evt);
    }
  };

  manageSelectedItem(evt) {
    if (isVerticalMovementKey(evt.key)) {
      this.handleVerticalPress(evt);
    } else if (this.allowHoritzontalNavigation) {
      this.handleHorizontalPress(evt);
    }
  }

  async openWithKeyboard(evt) {
    if (!this.canTriggerKeyboardOpen(evt.key)) return;

    evt.preventDefault();
    this.handleOpen();

    if (!isSelectionKey(evt.key)) {
      // give time for menu to open before trying to select item
      await waitForLater(MENU_OPEN_DELAY);

      this.manageSelectedItem(evt);
    }
  }

  parseKeyAction(evt) {
    let { key } = evt;

    if (!isCapturable(key)) return;
    if (!this.hasCustomAction(key)) return;

    evt.preventDefault();

    if (isSelectionKey(key)) {
      this.getActiveOption().click();
    } else if (isCloseKey(key)) {
      this.handleClose();
    } else {
      this.manageSelectedItem(evt);
    }
  }

  setBasicSelection(value) {
    if (this.isCurrentValue(value)) return;

    let currentSelection = this.menuNavigableOptions.find(option =>
      [option.dataset.value, option.textContent.trim()].includes(this.currentValue)
    );

    currentSelection?.setAttribute('aria-selected', 'false');

    this.value = value;

    this.selected = this.menuNavigableOptions.find(option =>
      [option.dataset.value, option.textContent.trim()].includes(value)
    );

    this.selected?.setAttribute('aria-selected', 'true');
  }

  async updateValue() {
    let { multiselect, value } = this.args;

    await waitForNext();

    if (multiselect) {
      this.selected = this.selectedCheckboxes;
    } else {
      this.setBasicSelection(value);
    }
  }

  updateVerticalSelection(index) {
    let lastIndex = this.navigableOptions.length - 1;

    if (index < -1) {
      this.updateFocus(lastIndex);
    } else if (!indexInRange(this.navigableOptions.length, index)) {
      this.focusTrigger();
    } else {
      this.updateFocus(index);
    }
  }

  updateFocus(index) {
    this.activeOption = this.navigableOptions[index];
    updateElementFocus(this.activeOption);
  }

  setup = async () => {
    await this.updateValue();

    this.baseElement.addEventListener('keydown', this.keyboardNav);
  };
}
